import React, { useContext, useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Transition, TransitionGroup } from 'react-transition-group'
import { gsap, ScrollSmoother, ScrollTrigger } from 'gsap-wrapper'
import LayoutProvider, { LayoutContext } from 'styles/layout-provider'

import CookiesBanner from 'components/ui-elements/cookies-banner'
import 'styles/fonts.css'
import GlobalStyle from 'styles/global-styles'
import { LayoutWrapper, PageWrapper, ContentWrapper } from './index.styled'
import useWindowSize from 'styles/window-size'

const LayoutContent = ({ children, location, className }) => {
  const { scrollSmoother } = useContext(LayoutContext)
  const { width } = useWindowSize()

  return (
    <div className={className}>
      <TransitionGroup component={null}>
        <Transition
          key={location.pathname}
          timeout={{ enter: 100, exit: 0 }}
          onEntered={() => {
            setTimeout(() => {
              if (width < 768) {
                if (scrollSmoother.current) scrollSmoother.current.scrollTop(0, false)
                else window.scrollTo(0, 0)
              }
            }, 10)
          }}
        >
          {(status) => (
            <div className={`page ${status}`}>
              <LayoutWrapper>{children}</LayoutWrapper>
            </div>
          )}
        </Transition>
      </TransitionGroup>
    </div>
  )
}

const useFrozenViewportHeight = () => {
  useLayoutEffect(() => {
    const apply = () => {
      const h = window.innerHeight
      if (h > 0) document.documentElement.style.setProperty('--panel-h', h + 'px')
    }
    apply()

    let lastWidth = window.innerWidth
    const onResize = () => {
      const heightOnly = window.innerWidth === lastWidth
      if (heightOnly && ScrollTrigger.isTouch === 1) return
      lastWidth = window.innerWidth
      apply()
      ScrollTrigger.refresh()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
}

const LayoutGsapWrapper = ({ children, location }) => {
  useFrozenViewportHeight()
  const { scrollSmoother } = useContext(LayoutContext)
  const refWrapper = useRef(null)

  useLayoutEffect(() => {
    const gsapContext = gsap.context(() => {
      scrollSmoother.current = ScrollSmoother.create({
        content: '.content',
        wrapper: '.wrapper',
        smooth: 2,
        smoothTouch: 0.1
      })
      scrollSmoother.current.scrollTo(0, false)
    }, [refWrapper, scrollSmoother])

    return () => gsapContext.revert()
  }, [children, scrollSmoother])

  return (
    <PageWrapper>
      <ContentWrapper className='wrapper' ref={refWrapper}>
        <LayoutContent
          location={location}
          className='content'
        >
          {children}
        </LayoutContent>
      </ContentWrapper>
    </PageWrapper>
  )
}

const Layout = ({ children, location }) => {
  return (
    <LayoutProvider location={location}>
      <GlobalStyle />
      <LayoutGsapWrapper location={location}>
        {children}
      </LayoutGsapWrapper>
      <CookiesBanner />
    </LayoutProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired
}

export default Layout
